import React from 'react';
import type { FormikProps } from 'formik';
import { Box, FormControl, InputV2 } from '@withjoy/joykit';
import { useId } from '@withjoy/joykit/hooks';
import { useTranslation } from '@shared/core';
import { PhoneField } from '@shared/components/IntlPhoneNumber/PhoneField';
import type { Contacts, FieldVisibility, IframeContacts } from '@apps/guest/contactCollector/shared/types';
import { RegistrationFieldLabel } from './RegistrationFieldLabel';

type RegistrationContactFieldsProps = Readonly<{
  formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
  /** Messages for fields with a problem, from the registration schema once the field is touched. */
  firstNameError?: string;
  lastNameError?: string;
  emailError?: string;
  phoneNumberRule: FieldVisibility;
  isPhoneNumberInvalid: boolean;
  setIsPhoneNumberValid: (isValid: boolean) => void;
  onPhoneBlur: () => void;
  phoneInputResetKey?: number;
  phoneCountryCode: string;
}>;

/**
 * The guest's own details on the registration contact step: first and last name (one row on
 * desktop), mobile phone with its country picker, and email. Labels follow the design: sentence
 * case, nothing on required fields, "optional" on an optional phone.
 */
export const RegistrationContactFields: React.FC<RegistrationContactFieldsProps> = ({
  formik,
  firstNameError,
  lastNameError,
  emailError,
  phoneNumberRule,
  isPhoneNumberInvalid,
  setIsPhoneNumberValid,
  onPhoneBlur,
  phoneInputResetKey,
  phoneCountryCode,
}) => {
  const { tq } = useTranslation('eventFormDisplay');
  const fieldId = useId(undefined, 'registration-contact');
  const phoneNumber = formik.values.contacts[0]?.phoneNumber;
  // Empty and required, or filled in wrong: two different things to tell the guest.
  const phoneError = isPhoneNumberInvalid ? (phoneNumber ? tq`Enter a valid phone number.` : tq`Enter your mobile phone number.`) : undefined;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={6}
      width="100%"
    >
      <FieldPair>
        <FormControl
          // Required for assistive tech; the label carries no mark (D2).
          isRequired
          label={<RegistrationFieldLabel text={tq`First name`} />}
          isInvalid={!!firstNameError}
          error={firstNameError}
        >
          <InputV2
            id={`${fieldId}-firstName`}
            {...formik.getFieldProps('contacts[0].firstName')}
            autoComplete="given-name"
          />
        </FormControl>
        <FormControl
          isRequired
          label={<RegistrationFieldLabel text={tq`Last name`} />}
          isInvalid={!!lastNameError}
          error={lastNameError}
        >
          <InputV2
            id={`${fieldId}-lastName`}
            {...formik.getFieldProps('contacts[0].lastName')}
            autoComplete="family-name"
          />
        </FormControl>
      </FieldPair>
      {phoneNumberRule !== 'hidden' && (
        <FormControl
          htmlFor={`${fieldId}-phone`}
          isRequired={phoneNumberRule === 'required'}
          label={
            <RegistrationFieldLabel
              text={tq`Mobile phone`}
              isOptional={phoneNumberRule === 'optional'}
            />
          }
          isInvalid={!!phoneError}
          error={phoneError}
        >
          <PhoneField
            id={`${fieldId}-phone`}
            resetKey={phoneInputResetKey}
            initialValue={phoneNumber}
            onChangeNumber={number => formik.setFieldValue('contacts[0].phoneNumber', number)}
            onChangeValidity={setIsPhoneNumberValid}
            onBlur={onPhoneBlur}
            initialCountry={phoneCountryCode}
            isInvalid={!!phoneError}
          />
        </FormControl>
      )}
      <FormControl
        isRequired
        label={<RegistrationFieldLabel text={tq`Email`} />}
        isInvalid={!!emailError}
        error={emailError}
      >
        <InputV2
          id={`${fieldId}-email`}
          {...formik.getFieldProps('contacts[0].email')}
          placeholder={tq`your@email.com`}
          type="email"
          inputMode="email"
          autoComplete="email"
        />
      </FormControl>
    </Box>
  );
};

/** Two short fields: stacked on phones, side by side on desktop. */
export const FieldPair: React.FC<Readonly<{ children: React.ReactNode }>> = ({ children }) => (
  <Box
    display="grid"
    gridTemplateColumns={['minmax(0, 1fr)', null, 'repeat(2, minmax(0, 1fr))']}
    columnGap={5}
    rowGap={6}
  >
    {children}
  </Box>
);
